import React, { useEffect, useRef, useState } from 'react';

import {
    Paper,
    Tabs,
    Tab,
    AppBar,
    makeStyles,
} from '@material-ui/core';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

import {
    getGrupos,
    getMembrosByGrupo,
    getRegistrosJ1,
    getFaltasAnoJ1,
    salvarFaltaJ1,
    removerFaltaJ1,
} from '../../services/GruposApi';
import FaltasJ1Table from '../../components/Grupo/FaltasJ1Table';
import InformationDialog from '../../components/InformationDialog';

// Converte 'yyyy-MM-dd' para 'dd/MM/yyyy' sem depender de timezone
const formatarData = dtEvento => {
    if (!dtEvento) {
        return '';
    }
    const [ano, mes, dia] = dtEvento.split('-');
    return `${dia}/${mes}/${ano}`;
};

// nb_tipo = 1 corresponde a JOVENS 1 (J1 / Nova Jovens 1) na tabela grupo_pequeno
const TIPO_J1 = 1;
// Ano corrente usado no histórico de faltas
const ANO_CORRENTE = new Date().getFullYear();
// getDay(): 6 = sábado. Os programas de jovens acontecem aos sábados.
const DIA_SABADO = 6;
// Tempo de inatividade (ms) após o qual a justificativa é salva automaticamente
const DEBOUNCE_JUSTIFICATIVA_MS = 800;

const useStyles = makeStyles(theme => ({
    root: {
        flexGrow: 1,
        width: '100%',
    },
    header: {
        padding: theme.spacing(2),
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 220,
    },
    tab: {
        backgroundColor: theme.palette.background.paper,
    },
    conteudo: {
        marginTop: theme.spacing(2),
    },
    carregando: {
        padding: theme.spacing(2),
    },
}));

function FaltasJ1() {
    const classes = useStyles();

    const [grupos, setGrupos] = useState([]);
    const [tabIndex, setTabIndex] = useState(0);
    const [membros, setMembros] = useState([]);
    const [registros, setRegistros] = useState([]);
    const [faltasAno, setFaltasAno] = useState([]);
    const [dataEvento, setDataEvento] = useState(null);
    const [processando, setProcessando] = useState(new Map());
    const [carregando, setCarregando] = useState(true);
    // Membro cujo histórico de faltas está aberto no diálogo (null = fechado)
    const [membroHistorico, setMembroHistorico] = useState(null);

    // Timers de debounce por membro (idMembro -> timeoutId)
    const debounceTimers = useRef(new Map());
    // Último texto digitado por membro (idMembro -> texto), para o timer ler o valor atual
    const justificativasPendentes = useRef(new Map());
    // Espelho sempre atualizado de `registros`, para leitura fora do ciclo de render
    const registrosRef = useRef([]);

    const grupoAtual = grupos[tabIndex];
    const dtEvento = dataEvento ? format(dataEvento, 'yyyy-MM-dd') : null;

    useEffect(() => {
        registrosRef.current = registros;
    }, [registros]);

    const setProcessandoMembro = (idMembro, valor) => {
        setProcessando(prev => {
            const novo = new Map(prev);
            novo.set(idMembro, valor);
            return novo;
        });
    };

    async function fetchGruposJ1() {
        try {
            const todos = await (await getGrupos()).data;
            const gruposJ1 = todos.filter(g => g.nbTipo === TIPO_J1);
            setGrupos(gruposJ1);
        } catch (err) {
            console.log(err);
        }

        setCarregando(false);
    }

    async function fetchMembros(idGrupoPequeno) {
        try {
            const t = Date.now();
            const resposta = await (await getMembrosByGrupo(idGrupoPequeno)).data;

            setMembros(
                resposta.map(m => ({
                    ...m,
                    txFotoUrl: m.txFotoUrl ? `${m.txFotoUrl}?t=${t}` : m.txFotoUrl,
                }))
            );
        } catch (err) {
            console.log(err);
            setMembros([]);
        }
    }

    async function fetchRegistros(idGrupoPequeno, data) {
        try {
            const resposta = await (await getRegistrosJ1(idGrupoPequeno, data)).data;
            setRegistros(resposta || []);
        } catch (err) {
            console.log(err);
            setRegistros([]);
        }
    }

    async function fetchFaltasAno(idGrupoPequeno) {
        try {
            const resposta = await (await getFaltasAnoJ1(idGrupoPequeno, ANO_CORRENTE)).data;
            setFaltasAno(resposta || []);
        } catch (err) {
            console.log(err);
            setFaltasAno([]);
        }
    }

    const handleTabChange = (_event, index) => {
        setTabIndex(index);
        setRegistros([]);
    };

    const handleDataChange = data => {
        setDataEvento(data);
    };

    const handleToggleFalta = async idMembro => {
        if (!grupoAtual || !dtEvento) {
            return;
        }

        const idGrupoPequeno = grupoAtual.idGrupoPequeno;
        // Lê do ref para ter sempre o estado mais recente (evita closure obsoleto)
        const faltava = registrosRef.current.some(r => r.idMembro === idMembro);

        // Cancela qualquer salvamento de justificativa pendente para este membro
        if (debounceTimers.current.has(idMembro)) {
            clearTimeout(debounceTimers.current.get(idMembro));
            debounceTimers.current.delete(idMembro);
        }
        justificativasPendentes.current.delete(idMembro);

        setProcessandoMembro(idMembro, true);

        try {
            if (faltava) {
                // Já faltava -> desmarcar (remover registro)
                await removerFaltaJ1(idMembro, idGrupoPequeno, dtEvento);
                setRegistros(prev => prev.filter(r => r.idMembro !== idMembro));
                // Mantém o total do ano em dia removendo a falta deste dia
                setFaltasAno(prev =>
                    prev.filter(
                        f => !(f.idMembro === idMembro && f.dtEvento === dtEvento)
                    )
                );
            } else {
                // Marcar falta (sem justificativa por enquanto)
                await salvarFaltaJ1(idMembro, idGrupoPequeno, dtEvento, '');
                setRegistros(prev => [
                    ...prev,
                    { idMembro, idGrupoPequeno, dtEvento, txJustificativa: '' },
                ]);
                // Reflete a nova falta no total do ano (se for do ano corrente)
                if (dtEvento.startsWith(`${ANO_CORRENTE}`)) {
                    setFaltasAno(prev => [
                        ...prev,
                        { idMembro, dtEvento, txJustificativa: '' },
                    ]);
                }
            }
        } catch (err) {
            console.log(err);
        }

        setProcessandoMembro(idMembro, false);
    };

    const salvarJustificativa = async (idMembro, valor) => {
        if (!grupoAtual || !dtEvento) {
            return;
        }

        setProcessandoMembro(idMembro, true);

        try {
            await salvarFaltaJ1(
                idMembro,
                grupoAtual.idGrupoPequeno,
                dtEvento,
                valor || ''
            );
            // Mantém a justificativa do histórico do ano sincronizada
            setFaltasAno(prev =>
                prev.map(f =>
                    f.idMembro === idMembro && f.dtEvento === dtEvento
                        ? { ...f, txJustificativa: valor || '' }
                        : f
                )
            );
        } catch (err) {
            console.log(err);
        }

        setProcessandoMembro(idMembro, false);
    };

    const handleJustificativaChange = (idMembro, valor) => {
        // Atualiza o estado local (controlled input)
        setRegistros(prev =>
            prev.map(r =>
                r.idMembro === idMembro ? { ...r, txJustificativa: valor } : r
            )
        );

        // Guarda o valor mais recente para o timer de debounce
        justificativasPendentes.current.set(idMembro, valor);

        // Reinicia o timer: só salva após o líder parar de digitar
        if (debounceTimers.current.has(idMembro)) {
            clearTimeout(debounceTimers.current.get(idMembro));
        }

        const timer = setTimeout(() => {
            debounceTimers.current.delete(idMembro);
            salvarJustificativa(idMembro, justificativasPendentes.current.get(idMembro));
        }, DEBOUNCE_JUSTIFICATIVA_MS);

        debounceTimers.current.set(idMembro, timer);
    };

    const handleJustificativaBlur = idMembro => {
        // Ao sair do campo, cancela o debounce pendente e salva imediatamente
        if (debounceTimers.current.has(idMembro)) {
            clearTimeout(debounceTimers.current.get(idMembro));
            debounceTimers.current.delete(idMembro);
        }

        const registro = registrosRef.current.find(r => r.idMembro === idMembro);

        if (!registro) {
            return;
        }

        salvarJustificativa(idMembro, registro.txJustificativa);
    };

    useEffect(() => {
        fetchGruposJ1();

        // Ao desmontar, cancela quaisquer timers de debounce pendentes
        const timers = debounceTimers.current;
        return () => {
            timers.forEach(timer => clearTimeout(timer));
            timers.clear();
        };
    }, []);

    useEffect(() => {
        if (grupoAtual) {
            fetchMembros(grupoAtual.idGrupoPequeno);
            fetchFaltasAno(grupoAtual.idGrupoPequeno);
        }
    }, [tabIndex, grupos]);

    useEffect(() => {
        if (grupoAtual && dtEvento) {
            fetchRegistros(grupoAtual.idGrupoPequeno, dtEvento);
        } else {
            setRegistros([]);
        }
    }, [tabIndex, grupos, dtEvento]);

    // Total de faltas do ano por membro (idMembro -> quantidade)
    const totaisFaltas = faltasAno.reduce((mapa, f) => {
        mapa.set(f.idMembro, (mapa.get(f.idMembro) || 0) + 1);
        return mapa;
    }, new Map());

    const handleVerHistorico = idMembro => {
        const membro = membros.find(m => m.idMembro === idMembro);
        setMembroHistorico(membro || null);
    };

    const handleFecharHistorico = () => {
        setMembroHistorico(null);
    };

    // Faltas do ano do membro selecionado, ordenadas da mais recente para a mais antiga
    const faltasDoMembro = membroHistorico
        ? faltasAno
              .filter(f => f.idMembro === membroHistorico.idMembro)
              .sort((a, b) => (a.dtEvento < b.dtEvento ? 1 : -1))
        : [];

    if (carregando) {
        return <div className={classes.carregando}>Carregando...</div>;
    }

    if (grupos.length === 0) {
        return (
            <div className={classes.carregando}>
                Você não possui grupos J1 (Nova Jovens 1) para gerenciar.
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <Paper>
                <div className={classes.header}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBR}>
                        <KeyboardDatePicker
                            className={classes.formControl}
                            disableToolbar
                            disableFuture
                            variant="inline"
                            format="dd/MM/yyyy"
                            label="Data (sábado)"
                            value={dataEvento}
                            onChange={handleDataChange}
                            shouldDisableDate={date =>
                                date && date.getDay() !== DIA_SABADO
                            }
                            autoOk
                            KeyboardButtonProps={{
                                'aria-label': 'selecionar data',
                            }}
                        />
                    </MuiPickersUtilsProvider>
                </div>

                <AppBar position="static" style={{ zIndex: 1000 }}>
                    <Tabs value={tabIndex} onChange={handleTabChange}>
                        {grupos.map((grupo, i) => (
                            <Tab
                                key={grupo.idGrupoPequeno}
                                value={i}
                                label={
                                    grupos.length > 1
                                        ? `GA DE ${grupo.txDiaSemanaReuniao}`
                                        : 'MEU GA'
                                }
                            />
                        ))}
                    </Tabs>
                </AppBar>
            </Paper>

            <div className={classes.conteudo}>
                <FaltasJ1Table
                    membros={membros}
                    registros={registros}
                    processando={processando}
                    dtEvento={dtEvento}
                    totaisFaltas={totaisFaltas}
                    ano={ANO_CORRENTE}
                    onToggleFalta={handleToggleFalta}
                    onJustificativaChange={handleJustificativaChange}
                    onJustificativaBlur={handleJustificativaBlur}
                    onVerHistorico={handleVerHistorico}
                />
            </div>

            <InformationDialog
                open={!!membroHistorico}
                handleClose={handleFecharHistorico}
                titulo={
                    membroHistorico
                        ? `Faltas de ${membroHistorico.txMembro} em ${ANO_CORRENTE}`
                        : ''
                }
            >
                {faltasDoMembro.length === 0 ? (
                    'Nenhuma falta registrada neste ano.'
                ) : (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {faltasDoMembro.map(f => (
                            <li key={f.dtEvento}>
                                <strong>{formatarData(f.dtEvento)}</strong>
                                {f.txJustificativa
                                    ? ` — ${f.txJustificativa}`
                                    : ' — (sem justificativa)'}
                            </li>
                        ))}
                    </ul>
                )}
            </InformationDialog>
        </div>
    );
}

export default FaltasJ1;
